import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, ChevronDown, History, Shield, Users, Lock } from 'lucide-react';
import { KanbanTask, DataScope } from '@/types'; 

interface InlineAddGroceryProps {
  onAdd: (name: string, quantity: number, unit: string, scope: 'personal' | 'household') => void;
  historyTasks: KanbanTask[]; 
  placeholder?: string;
  // 🛡️ Hybrid Scoping Props
  currentHouseholdId: string | null;
  activeDataScope: DataScope;
}

export const InlineAddGrocery: React.FC<InlineAddGroceryProps> = ({ 
  onAdd, 
  historyTasks,
  placeholder,
  currentHouseholdId,
  activeDataScope
}) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [unit, setUnit] = useState('pcs');
  const [showSuggestions, setShowSuggestions] = useState(false);
  
  // 🛡️ TARGET SCOPE STATE
  // Defaults to household if a household is active, otherwise locks to personal
  const [targetScope, setTargetScope] = useState<'personal' | 'household'>(
    currentHouseholdId ? 'household' : 'personal'
  );

  // --- 🧠 AUTOCOMPLETE LOGIC ---
  const suggestions = useMemo(() => {
    const input = value.toLowerCase().trim();
    if (!input || input.length < 2) return [];
    
    const uniqueNames = new Set<string>();
    return (historyTasks || [])
      .filter((task: KanbanTask) => {
        const name = task.title.toLowerCase();
        const matchesInput = name.includes(input) && !uniqueNames.has(name);
        
        // 🛡️ Scope Visibility Filter: Ensure suggestions align with active privacy view
        const isPersonal = task.scope === 'personal';
        let scopeMatches = true;
        if (activeDataScope === 'PERSONAL') scopeMatches = isPersonal;
        if (activeDataScope === 'SHARED') scopeMatches = !isPersonal;

        if (matchesInput && scopeMatches) {
          uniqueNames.add(name);
          return true;
        }
        return false;
      })
      .slice(0, 5); 
  }, [value, historyTasks, activeDataScope]);

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!value.trim()) return;
    
    // 🛡️ Enforce privacy: if no household, force personal scope
    const finalScope = currentHouseholdId ? targetScope : 'personal';
    onAdd(value.trim(), quantity, unit, finalScope);
    
    setValue('');
    setQuantity(1);
    setUnit('pcs');
    setShowSuggestions(false);
  };

  const selectSuggestion = (task: KanbanTask) => {
    setValue(task.title);
    if (task.consumption) {
      setUnit(task.consumption.unit || 'pcs');
      setQuantity(task.consumption.quantity || 1);
    }
    // Match the target scope to the suggestion origin for convenience
    if (task.scope) setTargetScope(task.scope as any);
    setShowSuggestions(false);
  };

  return (
    <form onSubmit={handleSubmit} className="relative group w-full space-y-3">
      <div className="relative">
        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 group-focus-within:text-indigo-500 transition-colors z-20">
          <Plus className="w-6 h-6" />
        </div>
        
        <input
          type="text"
          value={value}
          onFocus={() => setShowSuggestions(true)}
          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            setValue(e.target.value);
            setShowSuggestions(true);
          }}
          placeholder={placeholder || t('groceries.add_placeholder', 'Add new item...')}
          className="w-full pl-14 pr-4 py-4 bg-white dark:bg-slate-900 rounded-[1.5rem] shadow-sm border-2 border-slate-100 dark:border-slate-800 focus:border-indigo-500 focus:outline-none font-bold text-slate-900 dark:text-white placeholder:text-slate-400 transition-all text-lg"
        />

        {/* 🛰️ AUTOCOMPLETE DROPDOWN */}
        {showSuggestions && suggestions.length > 0 && (
          <div className="absolute top-[calc(100%+8px)] left-0 w-full bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 z-50 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="p-2 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <History className="w-3 h-3 text-slate-400" />
                <span className="text-[10px] font-black uppercase text-slate-400 tracking-tighter">
                  {t('groceries.from_history', 'Previously Purchased')}
                </span>
              </div>
              <Lock className="w-2.5 h-2.5 text-slate-300" />
            </div>
            <div className="max-h-60 overflow-y-auto no-scrollbar">
              {suggestions.map((task: KanbanTask) => (
                <button
                  key={task.id}
                  type="button"
                  onClick={() => selectSuggestion(task)}
                  className="w-full text-left px-5 py-3 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 flex justify-between items-center group/item transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {task.scope === 'personal' ? (
                      <Shield className="w-3 h-3 text-emerald-500" />
                    ) : (
                      <Users className="w-3 h-3 text-indigo-500" />
                    )}
                    <span className="font-bold text-slate-700 dark:text-slate-200">{task.title}</span>
                  </div>
                  {task.consumption && (
                    <span className="text-[10px] font-mono text-slate-400 group-hover/item:text-indigo-500">
                      {task.consumption.quantity} {task.consumption.unit}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* 🛠️ PRECISION CONTROLS & SCOPE TOGGLE */}
      <div className={`
        flex flex-wrap gap-2 transition-all duration-300 transform
        ${value.trim() ? 'opacity-100 translate-y-0 h-auto' : 'opacity-0 -translate-y-2 h-0 overflow-hidden'}
      `}>
        {/* 🛡️ SCOPE SELECTOR: Only visible if a household is active */}
        {currentHouseholdId && (
          <div className="flex bg-slate-100 dark:bg-slate-800 p-1 rounded-2xl border-2 border-transparent">
            <button
              type="button"
              onClick={() => setTargetScope('personal')}
              className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${targetScope === 'personal' ? 'bg-white dark:bg-slate-700 text-emerald-600 shadow-sm' : 'text-slate-400'}`}
            >
              <Shield className="w-3 h-3" />
              <span className="text-[9px] font-black uppercase">Personal</span>
            </button>
            <button
              type="button"
              onClick={() => setTargetScope('household')}
              className={`px-4 py-2 rounded-xl flex items-center gap-2 transition-all ${targetScope === 'household' ? 'bg-white dark:bg-slate-700 text-indigo-600 shadow-sm' : 'text-slate-400'}`}
            >
              <Users className="w-3 h-3" />
              <span className="text-[9px] font-black uppercase">Shared</span>
            </button>
          </div>
        )}

        <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-100 dark:border-slate-800 p-1 flex items-center min-w-[100px]">
          <span className="pl-3 text-[10px] font-black text-slate-400 uppercase tracking-tighter w-12">{t('common.qty', 'Qty')}</span>
          <input 
            type="number" 
            value={quantity}
            min={0.1}
            step={0.1}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuantity(Number(e.target.value))}
            className="w-full bg-transparent border-none focus:ring-0 font-black text-slate-800 dark:text-white text-center"
          />
        </div>

        <div className="flex-1 bg-white dark:bg-slate-900 rounded-2xl border-2 border-slate-100 dark:border-slate-800 p-1 flex items-center relative min-w-[100px]">
          <span className="pl-3 text-[10px] font-black text-slate-400 uppercase tracking-tighter w-12">{t('common.unit', 'Unit')}</span>
          <select 
            value={unit}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setUnit(e.target.value)}
            className="w-full bg-transparent border-none focus:ring-0 font-black text-slate-800 dark:text-white text-center appearance-none cursor-pointer pr-8"
          >
            {['pcs', 'kg', 'oz', 'ltr', 'gal', 'pack', 'doz', 'box'].map(u => (
              <option key={u} value={u}>{u}</option>
            ))}
          </select>
          <ChevronDown className="w-3 h-3 absolute right-3 text-slate-400 pointer-events-none" />
        </div>

        <button 
          type="submit"
          className={`px-6 py-4 rounded-2xl text-xs font-black uppercase tracking-widest shadow-lg transition-all active:scale-95 ${
            targetScope === 'personal' 
              ? 'bg-emerald-600 hover:bg-emerald-700 shadow-emerald-500/20' 
              : 'bg-indigo-600 hover:bg-indigo-700 shadow-indigo-500/20'
          } text-white`}
        >
          {t('common.add', 'Add')}
        </button>
      </div>
    </form>
  );
};